import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useMode } from './ModeContext';
import { 
  getUserEntities, 
  ensurePersonalEntity, 
  EntityServiceError 
} from '../services/entityService';
import { ENTITY_TYPES } from '../models/Entity';

// Create the context
const EntityContext = createContext();

// Hook to use the entity context
export const useEntity = () => {
  const context = useContext(EntityContext);
  if (!context) {
    throw new Error('useEntity must be used within an EntityProvider');
  }
  return context;
};

// EntityProvider component
export const EntityProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const { isSimpleMode, isAdvancedMode } = useMode();
  
  // State management
  const [entities, setEntities] = useState([]);
  const [currentEntity, setCurrentEntity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [initialized, setInitialized] = useState(false);

  /**
   * Sets the default current entity based on mode and available entities
   * @param {Array} userEntities - Available entities
   */
  const setDefaultCurrentEntity = useCallback((userEntities) => {
    if (userEntities.length === 0) return;

    // In Simple mode, always use Personal entity
    if (isSimpleMode) {
      const personalEntity = userEntities.find(e => e.type === ENTITY_TYPES.PERSONAL);
      setCurrentEntity(personalEntity || userEntities[0]);
      return;
    }

    // In Advanced mode, use the personal entity as default
    const personalEntity = userEntities.find(e => e.type === ENTITY_TYPES.PERSONAL);
    setCurrentEntity(personalEntity || userEntities[0]);
  }, [isSimpleMode]);

  /**
   * Loads all entities for the current user and ensures personal entity exists
   */
  const loadEntities = useCallback(async () => {
    if (!currentUser) return;

    try {
      setLoading(true);
      setError(null);

      // Ensure personal entity exists (creates if missing)
      await ensurePersonalEntity(currentUser.uid);

      // Load all entities
      const userEntities = await getUserEntities(currentUser.uid);
      setEntities(userEntities);

      // Set default current entity based on mode
      setDefaultCurrentEntity(userEntities);
      
      setInitialized(true);
    } catch (err) {
      console.error('Error loading entities:', err);
      setError(err instanceof EntityServiceError ? err.message : 'Failed to load entities');
    } finally {
      setLoading(false);
    }
  }, [currentUser, setDefaultCurrentEntity]);

  /**
   * Handles mode changes and adjusts entity selection
   */
  const handleModeChange = useCallback(() => {
    if (isSimpleMode) {
      // Switch to personal entity in Simple mode
      const personalEntity = entities.find(e => e.type === ENTITY_TYPES.PERSONAL);
      if (personalEntity && currentEntity?.id !== personalEntity.id) {
        setCurrentEntity(personalEntity);
      }
    }
    // In Advanced mode, keep current selection (user can change manually)
  }, [isSimpleMode, entities, currentEntity]);

  // Load entities when user changes or mode changes
  useEffect(() => {
    if (currentUser && !initialized) {
      loadEntities();
    } else if (!currentUser) {
      // Reset state when user logs out
      setEntities([]);
      setCurrentEntity(null);
      setInitialized(false);
      setError(null);
      setLoading(false);
    }
  }, [currentUser, initialized, loadEntities]);

  // Handle mode changes - adjust current entity selection
  useEffect(() => {
    if (entities.length > 0 && currentUser) {
      handleModeChange();
    }
  }, [isSimpleMode, isAdvancedMode, entities, currentUser, handleModeChange]);

  /**
   * Switches to a specific entity (Advanced mode only)
   * @param {string} entityId - Entity ID to switch to
   */
  const switchToEntity = (entityId) => {
    if (isSimpleMode) {
      console.warn('Cannot switch entities in Simple mode');
      return;
    }

    const entity = entities.find(e => e.id === entityId);
    if (entity) {
      setCurrentEntity(entity);
      // TODO: Persist selection to localStorage
    } else {
      console.error('Entity not found:', entityId);
    }
  };

  /**
   * Refreshes the entities list
   */
  const refreshEntities = async () => {
    if (currentUser) {
      await loadEntities();
    }
  };

  /**
   * Adds a new entity to the local state (called after successful creation)
   * @param {Object} newEntity - Newly created entity
   */
  const addEntity = (newEntity) => {
    setEntities(prev => [newEntity, ...prev]);
    
    // If this is the first entity or Advanced mode, switch to it
    if (entities.length === 0 || isAdvancedMode) {
      setCurrentEntity(newEntity);
    }
  };

  /**
   * Updates an entity in the local state
   * @param {string} entityId - Entity ID to update
   * @param {Object} updatedEntity - Updated entity data
   */
  const updateEntity = (entityId, updatedEntity) => {
    setEntities(prev => prev.map(entity => 
      entity.id === entityId ? updatedEntity : entity
    ));

    // Update current entity if it's the one being updated
    if (currentEntity?.id === entityId) {
      setCurrentEntity(updatedEntity);
    }
  };

  /**
   * Removes an entity from the local state
   * @param {string} entityId - Entity ID to remove
   */
  const removeEntity = (entityId) => {
    setEntities(prev => prev.filter(entity => entity.id !== entityId));

    // If current entity was deleted, switch to another one
    if (currentEntity?.id === entityId) {
      const remainingEntities = entities.filter(e => e.id !== entityId);
      setDefaultCurrentEntity(remainingEntities);
    }
  };

  /**
   * Gets entities filtered by type
   * @param {string} entityType - Entity type to filter by
   * @returns {Array} Filtered entities
   */
  const getEntitiesByType = (entityType) => {
    return entities.filter(entity => entity.type === entityType);
  };

  /**
   * Gets active entities only
   * @returns {Array} Active entities
   */
  const getActiveEntities = () => {
    return entities.filter(entity => entity.status === 'active');
  };

  /**
   * Checks if user has entities of a specific type
   * @param {string} entityType - Entity type to check
   * @returns {boolean} True if user has entities of this type
   */
  const hasEntitiesOfType = (entityType) => {
    return entities.some(entity => entity.type === entityType);
  };

  const value = {
    // State
    entities,
    currentEntity,
    loading,
    error,
    initialized,

    // Actions
    loadEntities,
    refreshEntities,
    switchToEntity,
    addEntity,
    updateEntity,
    removeEntity,

    // Getters
    getEntitiesByType,
    getActiveEntities,
    hasEntitiesOfType,

    // Computed values
    entityCount: entities.length,
    activeEntityCount: entities.filter(e => e.status === 'active').length,
    personalEntity: entities.find(e => e.type === ENTITY_TYPES.PERSONAL),
    
    // Mode-aware computed values
    isPersonalEntitySelected: currentEntity?.type === ENTITY_TYPES.PERSONAL,
    canSwitchEntities: isAdvancedMode && entities.length > 1,
    availableEntities: isSimpleMode 
      ? entities.filter(e => e.type === ENTITY_TYPES.PERSONAL)
      : entities
  };

  return (
    <EntityContext.Provider value={value}>
      {children}
    </EntityContext.Provider>
  );
};

export default EntityContext; 